import { Routes } from '@angular/router';
import { AuctionResolver } from './resolvers/auction-resolver';
import { resolveHomeAuctions } from './resolvers/home-auctions-resolver';
import { authGuard } from './services/auth-guard.service';
import { loginGuard } from './services/login-guard.service';
import { RasmusGuard } from './services/rasmus-guard';
import { loadAuctionsComponent } from './auctions/auctions-route-loader';
import { loadItemComponent } from './auctions/item-route-loader';
import { loadRecordComponent } from './auctions/record-route-loader';

export const routes: Routes = [
  {
    path: '',
    loadComponent: loadAuctionsComponent,
    resolve: {
      homeAuctions: resolveHomeAuctions,
    },
  },
  {
    path: 'search',
    loadComponent: loadAuctionsComponent,
    resolve: {
      homeAuctions: resolveHomeAuctions,
    },
  },
  {
    path: 'rasmus-tv',
    loadComponent: () => import('./rasmus-tv/rasmus-tv.component'),
    canActivate: [RasmusGuard],
  },
  { path: 'buyers', loadComponent: () => import('./wp-pages/buyers/buyers.component'), canActivate: [RasmusGuard] },
  { path: 'sellers', loadComponent: () => import('./wp-pages/sellers/sellers.component'), canActivate: [RasmusGuard] },
  { path: 'real-estate', loadComponent: () => import('./wp-pages/real-estate/real-estate.component'), canActivate: [RasmusGuard] },
  { path: 'referrals', loadComponent: () => import('./wp-pages/referrals/referrals.component'), canActivate: [RasmusGuard] },
  { path: 'contact', loadComponent: () => import('./wp-pages/contact-us/contact-us.component'), canActivate: [RasmusGuard] },
  { path: 'about-us', loadComponent: () => import('./wp-pages/about-us/about-us.component'), canActivate: [RasmusGuard] },
  { path: 'knowledge-base', loadComponent: () => import('./wp-pages/help/help.component'), canActivate: [RasmusGuard] },
  { path: 'blog', loadComponent: () => import('./blogs/blog-iframe.component'), canActivate: [RasmusGuard] },
  { path: 'gaming-faq', loadComponent: () => import('./gaming/gaming.component'), canActivate: [RasmusGuard] },
  { path: 'privacy-policy', loadComponent: () => import('./wp-pages/privacy-policy/privacy-policy.component'), canActivate: [RasmusGuard] },
  { path: 'sms-signup', loadComponent: () => import('./users/opt-in/opt-in.component'), canActivate: [RasmusGuard] },
  { path: 'dcma', loadComponent: () => import('./wp-pages/dmca/dmca.component'), canActivate: [RasmusGuard] },
  { path: 'terms', loadComponent: () => import('./wp-pages/terms/terms.component'), canActivate: [RasmusGuard] },
  { path: 'thank-you', loadComponent: () => import('./thank-you.component'), canActivate: [RasmusGuard] },
  { path: 'thank-you-seller', loadComponent: () => import('./thank-you.component'), canActivate: [RasmusGuard] },
  { path: 'thank-you-referral', loadComponent: () => import('./thank-you.component'), canActivate: [RasmusGuard] },
  { path: 'products/:iid/:name', loadComponent: loadItemComponent },
  {
    path: 'auctions',
    // prettier-ignore
    children: [
      { loadComponent: loadRecordComponent, path: ':aid/a/:label', resolve: { auction: AuctionResolver } },
      { loadComponent: loadRecordComponent, path: ':aid', resolve: { auction: AuctionResolver } },
      { loadComponent: loadRecordComponent, path: ':aid/seller/:seller', resolve: { auction: AuctionResolver } },
      { loadComponent: loadRecordComponent, path: ':aid/unlisted/:hash', resolve: { auction: AuctionResolver } },
      { loadComponent: loadRecordComponent, path: ':aid/lot/:lot', resolve: { auction: AuctionResolver } },
      { loadComponent: () => import('./auctions/schedule-loader.component'), path: ':aid/appointment' },
      { loadComponent: () => import('./auctions/schedule.component'), path: ':aid/appointment/:schid' },
      { loadComponent: loadItemComponent, path: ':aid/item/:iid' },
      { loadComponent: () => import('./auctions/refer-friend.component'), path: ':aid/refer-friend', canActivate: [authGuard] },
    ],
  },
  // prettier-ignore
  {
    path: 'user',
    children: [
      { path: 'opt-in', loadComponent: () => import('./users/opt-in/opt-in.component'), canActivate: [RasmusGuard] },
      { path: 'cards/:aid/add', loadComponent: () => import('./users/add-credit-card.component'), canActivate: [authGuard] },
      { path: 'cards/add', loadComponent: () => import('./users/add-credit-card.component'), canActivate: [authGuard] },
      { path: 'cards/view', loadComponent: () => import('./users/manage-credit-cards.component'), canActivate: [authGuard] },
      { path: 'cards/view/:aid', loadComponent: () => import('./users/manage-credit-cards.component'), canActivate: [authGuard] },
      { path: 'cards/view/:aid/orders/:oid', loadComponent: () => import('./users/manage-credit-cards.component'), canActivate: [authGuard] },
      { path: 'complete/:aid', loadComponent: () => import('./users/complete-profile.component'), canActivate: [authGuard] },
      { path: 'complete', loadComponent: () => import('./users/complete-profile.component'), canActivate: [authGuard] },
      { path: 'invoices/:oid', loadComponent: () => import('./invoices/order.component'), canActivate: [authGuard] },
      { path: 'profile', loadComponent: () => import('./users/user-profile.component'), canActivate: [authGuard] },
      { path: 'profile/password', loadComponent: () => import('./users/user-profile.component'), canActivate: [authGuard] },
      { path: 'profile/invoices', loadComponent: () => import('./users/user-profile.component'), canActivate: [authGuard] },
      { path: 'verification/:aid', loadComponent: () => import('./users/verify.component'), canActivate: [authGuard] },

      { path: 'register', loadComponent: () => import('./users/register/register.component').then((m) => m.RegisterComponent) },
      { path: 'register/:step', loadComponent: () => import('./users/register/register.component').then((m) => m.RegisterComponent) },
      { path: 'login', loadComponent: () => import('./users/login-modal.component').then((m) => m.LoginModalComponent), canActivate: [loginGuard] },
      { path: 'redeem-link', loadComponent: () => import('./users/redeem-referral.component'), canActivate: [authGuard] },
    ],
  },
  {
    path: 'auction',
    // prettier-ignore
    children: [
      { path: 'buyers', loadComponent: () => import('./wp-pages/buyers/buyers.component'), canActivate: [RasmusGuard] },
      { path: 'sellers', loadComponent: () => import('./wp-pages/sellers/sellers.component'), canActivate: [RasmusGuard] },
      { path: 'real-estate', loadComponent: () => import('./wp-pages/real-estate/real-estate.component'), canActivate: [RasmusGuard] },
      { path: 'referrals', loadComponent: () => import('./wp-pages/referrals/referrals.component'), canActivate: [RasmusGuard] },
      { path: 'contact', loadComponent: () => import('./wp-pages/contact-us/contact-us.component'), canActivate: [RasmusGuard] },
      { path: 'about-us', loadComponent: () => import('./wp-pages/about-us/about-us.component'), canActivate: [RasmusGuard] },
      { path: 'knowledge-base', loadComponent: () => import('./wp-pages/help/help.component'), canActivate: [RasmusGuard] },
      { path: 'blog', loadComponent: () => import('./blogs/blog-iframe.component'), canActivate: [RasmusGuard] },
      { path: 'gaming-faq', loadComponent: () => import('./gaming/gaming.component'), canActivate: [RasmusGuard] },
      { path: 'privacy-policy', loadComponent: () => import('./wp-pages/privacy-policy/privacy-policy.component'), canActivate: [RasmusGuard] },
      { path: 'sms-signup', loadComponent: () => import('./users/opt-in/opt-in.component'), canActivate: [RasmusGuard] },
      { path: 'dcma', loadComponent: () => import('./wp-pages/dmca/dmca.component'), canActivate: [RasmusGuard] },
      { path: 'terms', loadComponent: () => import('./wp-pages/terms/terms.component'), canActivate: [RasmusGuard] },
      { path: 'thank-you', loadComponent: () => import('./thank-you.component'), canActivate: [RasmusGuard] },
      { path: 'thank-you-seller', loadComponent: () => import('./thank-you.component'), canActivate: [RasmusGuard] },
      { path: 'thank-you-referral', loadComponent: () => import('./thank-you.component'), canActivate: [RasmusGuard] },
      { path: ':slug', loadComponent: () => import('./page.component'), canActivate: [RasmusGuard] },
    ],
  },
  // prettier-ignore
  { path: 'orders/:oid', redirectTo: 'invoices/:oid' },
  // prettier-ignore
  { path: 'invoices/:oid', loadComponent: () => import('./invoices/order.component'), canActivate: [authGuard] },
  { path: '**', loadComponent: () => import('./not-found.component').then((m) => m.NotFoundComponent) },
];
